"use client";

import React, { createContext, useEffect, useState, useCallback, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { makeAuthenticatedQuickBooksGet } from "@/lib/utils/api/api-with-auth";

/**
 * Route prefixes where QuickBooks state (connection status, active realm,
 * company info) is actually rendered — invoicing, billing reports, recurring
 * charges, deal→invoice, company↔QB-customer linking, and admin. The provider
 * is mounted once at the app root; previously it probed `/api/quickbooks/connection`
 * on the FIRST load of every route (chat, tickets, tasks, time entries,
 * contacts, the landing page — none of which show QB UI), adding an auth-gated
 * round-trip to the critical path. Now the probe only fires on these routes and
 * re-fires when the user navigates onto one. `refreshConnection` still lets any
 * consumer force a fetch on demand.
 */
const QB_ROUTE_PREFIXES = [
  "/invoices",
  "/recurring-charges",
  "/reports",
  "/admin",
  "/deals",
  "/companies",
];
import type {
  QuickBooksTokens,
  QuickBooksCompanyInfo,
  QuickBooksContextType,
  QuickBooksConnections,
  QuickBooksConnectionData,
} from "@/lib/types/quickbooks-unified";

export const QuickBooksContext = createContext<QuickBooksContextType | undefined>(undefined);

export function QuickBooksProvider({ children }: { children: React.ReactNode }) {
  const { appUser, session, isLoading: isAuthLoading } = useUniversalAuth();
  const pathname = usePathname();
  const onQuickBooksRoute = QB_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname?.startsWith(`${prefix}/`)
  );

  const [isConnected, setIsConnected] = useState(false);
  const [tokens] = useState<QuickBooksTokens | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connections, setConnections] = useState<QuickBooksConnections>({});
  const [activeRealmId, setActiveRealmId] = useState<string | null>(null);
  const [activeConnection, setActiveConnection] = useState<QuickBooksConnectionData | null>(null);
  const [connectionCount, setConnectionCount] = useState(0);
  const [companyInfo, setCompanyInfo] = useState<QuickBooksCompanyInfo | null>(null);

  const fetchConnectionStatus = useCallback(async () => {
    const authReady = !isAuthLoading && !!session && !!appUser;
    if (!authReady || !session?.access_token) {
      setIsConnected(false);
      setCompanyInfo(null);
      setError(null);
      return;
    }

    try {
      const apiCall = makeAuthenticatedQuickBooksGet("connection", session.access_token);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("QuickBooks API call timeout after 10 seconds")), 10000)
      );
      const data = (await Promise.race([apiCall, timeoutPromise])) as any;
      const connected = !!data.isConnected;
      setIsConnected(connected);
      setError(null);

      if (data.connections) {
        setConnections(data.connections);
        setConnectionCount(data.connectionCount || 0);

        if (data.activeRealmId && data.connections[data.activeRealmId]) {
          const conn = data.connections[data.activeRealmId];
          setCompanyInfo({
            CompanyName: conn.company_name || `Company ${data.activeRealmId.slice(-4)}`,
            CompanyAddr: undefined,
            PrimaryPhone: undefined,
            CompanyStartDate: undefined,
            FiscalYearStartMonth: undefined,
            Country: undefined,
            Email: undefined,
            WebAddr: undefined,
            NameValue: [],
            domain: "QBO",
            sparse: false,
            Id: data.activeRealmId,
            SyncToken: "0",
            MetaData: {
              CreateTime: conn.connected_at || new Date().toISOString(),
              LastUpdatedTime: conn.connected_at || new Date().toISOString(),
            },
          });
        }
      }
      if (data.activeRealmId) {
        setActiveRealmId(data.activeRealmId);
        setActiveConnection(data.activeConnection || null);
      }

      if (!connected) {
        setCompanyInfo(null);
        setConnections({});
        setActiveRealmId(null);
        setActiveConnection(null);
        setConnectionCount(0);
      }
    } catch (err) {
      console.error("QuickBooks connection fetch error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch connection status";
      setError(errorMessage);
      setIsConnected(false);
      setCompanyInfo(null);
      setConnections({});
      setActiveRealmId(null);
      setActiveConnection(null);
      setConnectionCount(0);
    }
  }, [appUser?.id, session?.access_token, isAuthLoading]);

  useEffect(() => {
    // Only probe QB connection status on routes that actually render QB state.
    if (!onQuickBooksRoute) return;
    void fetchConnectionStatus();
  }, [onQuickBooksRoute, fetchConnectionStatus]);

  const setActiveRealm = useCallback(
    async (realmId: string) => {
      const authReady = !isAuthLoading && !!session && !!appUser;
      if (!authReady || !session?.access_token) return;

      try {
        const response = await fetch("/api/quickbooks/connection", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: "set_active_realm", realmId }),
        });

        if (response.ok) {
          setActiveRealmId(realmId);
          setActiveConnection(connections[realmId] || null);

          if (connections[realmId]) {
            const conn = connections[realmId];
            setCompanyInfo({
              CompanyName: conn.company_name || `Company ${realmId.slice(-4)}`,
              CompanyAddr: undefined,
              PrimaryPhone: undefined,
              CompanyStartDate: undefined,
              FiscalYearStartMonth: undefined,
              Country: undefined,
              Email: undefined,
              WebAddr: undefined,
              NameValue: [],
              domain: "QBO",
              sparse: false,
              Id: realmId,
              SyncToken: "0",
              MetaData: {
                CreateTime: conn.connected_at || new Date().toISOString(),
                LastUpdatedTime: conn.connected_at || new Date().toISOString(),
              },
            });
          }

          void fetchConnectionStatus();
        } else {
          console.error("Failed to set active realm:", await response.text());
        }
      } catch (error) {
        console.error("Error setting active realm:", error);
      }
    },
    [isAuthLoading, session, appUser, connections, fetchConnectionStatus]
  );

  const refreshConnectionMemoized = useCallback(() => {
    void fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  const contextValue: QuickBooksContextType = useMemo(
    () => ({
      isConnected,
      isLoading: isAuthLoading,
      error,
      connections,
      activeRealmId,
      activeConnection,
      connectionCount,
      tokens,
      companyInfo,
      isLoadingCompanyInfo: false,
      companyInfoError: null,
      refreshConnection: refreshConnectionMemoized,
      refreshCompanyInfo: () => {},
      retryCompanyInfo: () => {},
      setActiveRealm,
    }),
    [
      isConnected,
      isAuthLoading,
      error,
      connections,
      activeRealmId,
      activeConnection,
      connectionCount,
      tokens,
      companyInfo,
      refreshConnectionMemoized,
      setActiveRealm,
    ]
  );

  return <QuickBooksContext.Provider value={contextValue}>{children}</QuickBooksContext.Provider>;
}
